import React, { useEffect, useRef, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { Music, VolumeX } from "lucide-react";

// Site-wide background music. Plays a per-language looped track at a low volume.
// Default ON. Browsers block audio with sound until the first user gesture, so we
// attempt to play immediately on load and keep retrying on every interaction.
export default function BackgroundMusic() {
  const { settings, lang, langConfirmed } = useApp();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [userOn, setUserOn] = useState(() => {
    const v = localStorage.getItem("bgMusicOn");
    return v === null ? true : v === "true";
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const path = lang === "en" ? settings?.music_path_en : settings?.music_path_pt;
  const src = path ? `${backendUrl}/api/files/${path}` : "";
  const volume = Math.min(1, Math.max(0, (settings?.music_volume ?? 20) / 100));
  const enabled = !!settings?.music_enabled && !!src && userOn && langConfirmed;
  // Keep the latest `enabled` available to native media events (avoids stale closures).
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Try to play whenever the source/enabled/volume changes (best-effort autoplay).
  // When the language (and therefore the src) changes, reload so the audio element
  // switches to the newly selected language track instead of keeping the buffered one.
  const prevSrcRef = useRef("");
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      a.load(); // load the track for the selected language (PT/EN)
    }
    if (enabled) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [enabled, volume, src]);

  // Retry on the first user gesture (required by browser autoplay policies).
  useEffect(() => {
    const tryPlay = () => {
      const a = audioRef.current;
      if (a && enabled && a.paused) a.play().catch(() => {});
    };
    const events = ["pointerdown", "click", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, tryPlay, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, tryPlay));
  }, [enabled]);

  if (!settings?.music_enabled || !src || !langConfirmed) return null;

  const toggle = () => {
    const next = !userOn;
    setUserOn(next);
    localStorage.setItem("bgMusicOn", String(next));
    const a = audioRef.current;
    if (a) {
      if (next) a.play().catch(() => {});
      else a.pause();
    }
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        loop
        preload="auto"
        onPlay={() => {
          // Guard against stray/native autoplay (e.g. the audio channel being
          // unlocked by the Nevão wind on mobile) while the user has music OFF.
          if (!enabledRef.current) {
            audioRef.current?.pause();
            return;
          }
          setPlaying(true);
        }}
        onPlaying={() => {
          if (!enabledRef.current) {
            audioRef.current?.pause();
            return;
          }
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
      />
      <button
        onClick={toggle}
        data-testid="bg-music-toggle"
        aria-label={userOn ? "Desligar música" : "Ligar música"}
        title={userOn ? "Desligar música de fundo" : "Ligar música de fundo"}
        className={`fixed bottom-4 left-4 z-[9998] h-12 w-12 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 ${userOn ? "bg-[#C1121F] text-white" : "bg-white text-[#0B1D3A] border border-[#0B1D3A]/15"}`}
      >
        {userOn && playing ? (
          <span className="eq" data-testid="bg-music-eq" aria-hidden="true">
            <span className="eq-bar" />
            <span className="eq-bar" />
            <span className="eq-bar" />
            <span className="eq-bar" />
          </span>
        ) : userOn ? (
          <Music size={20} className="animate-pulse" />
        ) : (
          <VolumeX size={20} />
        )}
      </button>
    </>
  );
}
